// src/App.js
import { Suspense, lazy, useEffect, useState } from "react";
import { Route, Navigate, useParams } from "react-router-dom";

import Loader from "./components/Loader";
import Layout from "./layout/Layout";
import { clearSalesSession, getSalesAuthToken, getSalesSession, setSalesUser } from "./services/salesTeamApi";
import { permissionService } from "./services/permissionService";

function LegacyBrandSetPasswordRedirect() {
  const { token } = useParams();
  return <Navigate to={`/nyafaz-brand/set-password/${token}`} replace />;
}

function LegacyBrandVerifyRedirect() {
  const { token } = useParams();
  return <Navigate to={`/nyafaz-brand/verify/${token}`} replace />;
}

const LoginSign = lazy(() => import("./pages/LoginSign"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Verify = lazy(() => import("./pages/Verify"));
const ProductList = lazy(() => import("./pages/ProductList"));
const Addproduct = lazy(() => import("./pages/Addproduct"));
const Setflow = lazy(() => import("./pages/Setflow"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Settings = lazy(() => import("./pages/Settings"));
const Permission = lazy(() => import("./pages/Permission"));
const AddPlan = lazy(() => import("./pages/AddPlan"));
const IbDashboard = lazy(() => import("./pages/IbDashboard"));
const AdminOpsDashboard = lazy(() => import("./pages/AdminOpsDashboard"));
const Contact = lazy(() => import("./pages/Contact"));
const AiAnalytics = lazy(() => import("./pages/AiAnalytics"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const AllUsersInfo = lazy(() => import("./pages/AllUsersInfo"));
const EmbedScript = lazy(() => import("./pages/EmbedScript"));
const UserList = lazy(() => import("./pages/UserList"));
const OrganizationList = lazy(() => import("./pages/OrganizationList"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const PaymentRequests = lazy(() => import("./pages/PaymentRequests"));
const SubscriptionDetail = lazy(() => import("./pages/SubscriptionDetail"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const PaymentDetail = lazy(() => import("./pages/PaymentDetail"));
const AdminContactMessages = lazy(() => import("./pages/AdminContactMessages"));
const SalesTeamCreateUser = lazy(() => import("./pages/SalesTeamCreateUser"));
const SalesUsersList = lazy(() => import("./pages/SalesUsersList"));
const SalesSharedLinks = lazy(() => import("./pages/SalesSharedLinks"));
const SalesRequestUsers = lazy(() => import("./pages/SalesRequestUsers"));
const SalesLogin = lazy(() => import("./pages/SalesLogin"));
const SalesUserDashboard = lazy(() => import("./pages/SalesUserDashboard"));
const WhatsAppTemplateManager = lazy(() => import("./pages/WhatsAppTemplateManager"));
const WalletTopup = lazy(() => import("./pages/WalletTopup"));
const AccountBilling = lazy(() => import("./pages/AccountBilling"));

const RouteFallback = () => <Loader fullScreen />;

const withSuspense = (node) => <Suspense fallback={<RouteFallback />}>{node}</Suspense>;

const UserDashboardRoute = () => {
  const isAdmin = permissionService.isPlatformAdmin();
  return isAdmin ? <Navigate to="/nyafaz-brand/admin-ops-dashboard" replace /> : withSuspense(<IbDashboard />);
};

const AdminDashboardRoute = ({ children }) => {
  const isAdmin = permissionService.isPlatformAdmin();
  if (!isAdmin) {
    return <Navigate to="/nyafaz-brand/ib-dashboard" replace />;
  }

  return children;
};

const AdminOnlyRoute = ({ children }) => {
  const isAdmin = permissionService.isPlatformAdmin();
  return isAdmin ? children : <Navigate to="/nyafaz-brand/ib-dashboard" replace />;
};

const SalesProtectedRoute = ({ children }) => {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let alive = true;

    const validate = async () => {
      const token = getSalesAuthToken();
      if (!token) {
        if (alive) setStatus("invalid");
        return;
      }

      try {
        const data = await getSalesSession();
        if (!alive) return;
        setSalesUser(data.sales_user);
        setStatus("valid");
      } catch {
        clearSalesSession();
        if (alive) setStatus("invalid");
      }
    };

    validate();

    return () => {
      alive = false;
    };
  }, []);

  if (status === "checking") {
    return <RouteFallback />;
  }

  return status === "valid" ? children : <Navigate to="/nyafaz-sales/login" replace />;
};

export function renderBrandAdminRoutes({ brandAdminHome = false } = {}) {
  return (
    <>
      {brandAdminHome ? <Route path="/" element={<Navigate to="/nyafaz-brand" replace />} /> : null}

      {/* Redirect old routes */}
        <Route path="/productlist" element={<Navigate to="/nyafaz-brand/productlist" replace />} />
        <Route path="/add-product" element={<Navigate to="/nyafaz-brand/add-product" replace />} />
        <Route path="/edit-product" element={<Navigate to="/nyafaz-brand/edit-product" replace />} />
        <Route path="/view-product" element={<Navigate to="/nyafaz-brand/view-product" replace />} />
        <Route path="/overview" element={<Navigate to="/nyafaz-brand/overview" replace />} />
        <Route path="/ib-dashboard" element={<Navigate to="/nyafaz-brand/ib-dashboard" replace />} />
        {/* <Route path="/verify/:token" element={<Navigate to="/nyafaz-brand/verify/:token" replace />} /> */}
        {/* <Route path="/verify" element={<Navigate to="/nyafaz-brand/verify" replace />} /> */}
        <Route path="/brandflow" element={<Navigate to="/nyafaz-brand/brandflow" replace />} />
        <Route path="/edit-profile" element={<Navigate to="/nyafaz-brand/edit-profile" replace />} />
        <Route path="/plan-details" element={<Navigate to="/nyafaz-brand/account-billing?tab=plan" replace />} />
        <Route path="/wallet" element={<Navigate to="/nyafaz-brand/account-billing?tab=credits" replace />} />
        <Route path="/embed-script" element={<Navigate to="/nyafaz-brand/embed-script" replace />} />
        <Route path="/contact-us" element={<Navigate to="/nyafaz-brand/contact-us" replace />} />
        <Route path="/organization-permission" element={<Navigate to="/nyafaz-brand/organization-permission" replace />} />
        <Route path="/add-plan" element={<Navigate to="/nyafaz-brand/add-plan" replace />} />
        <Route path="/organization" element={<Navigate to="/nyafaz-brand/organization" replace />} />
        <Route path="/brand-users" element={<Navigate to="/nyafaz-brand/brand-users" replace />} />
        <Route path="/invoices" element={<Navigate to="/nyafaz-brand/invoices" replace />} />
        <Route path="/subscriptions" element={<Navigate to="/nyafaz-brand/subscriptions" replace />} />
        <Route path="/payment-requests" element={<Navigate to="/nyafaz-brand/payment-requests" replace />} />
        <Route path="/admin-contact-messages" element={<Navigate to="/nyafaz-brand/admin-contact-messages" replace />} />
        {/* Pages without layout */}
        <Route path="/nyafaz-brand" element={withSuspense(<LoginSign />)} />
        <Route path="/nyafaz-sales" element={<Navigate to="/nyafaz-sales/login" replace />} />
        <Route path="/nyafaz-sales/login" element={withSuspense(<SalesLogin />)} />
        <Route
          path="/nyafaz-sales/dashboard"
          element={<SalesProtectedRoute>{withSuspense(<SalesUserDashboard />)}</SalesProtectedRoute>}
        />
        <Route path="/nyafaz-sales/*" element={<Navigate to="/nyafaz-sales/login" replace />} />
        <Route
          path="/nyafaz-brand/nyafaz-brand/set-password/:token"
          element={<LegacyBrandSetPasswordRedirect />}
        />
        <Route
          path="/nyafaz-brand/nyafaz-brand/verify/:token"
          element={<LegacyBrandVerifyRedirect />}
        />
        <Route
          path="/nyafaz-brand/set-password/:token"
          element={withSuspense(<SetPassword />)}
        />
        <Route path="/nyafaz-brand/verify/:token" element={withSuspense(<Verify />)} />
        <Route path="/verify/:token" element={withSuspense(<Verify />)} />
        <Route path="/verify" element={withSuspense(<Verify />)} />

        {/* Pages with Layout */}
        <Route
          path="/nyafaz-brand/overview"
          element={
            <Layout hideSidebar={true} dashboardOnly={true}>
              <Suspense fallback={<RouteFallback />}>
                <Dashboard />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/ib-dashboard"
          element={
            <Layout>
              <UserDashboardRoute />
            </Layout>

          }
        />
        <Route
          path="/nyafaz-brand/admin-ops-dashboard"
          element={
            <Layout>
              <AdminDashboardRoute>{withSuspense(<AdminOpsDashboard />)}</AdminDashboardRoute>
            </Layout>

          }
        />
        <Route
          path="/nyafaz-brand/admin-dashboard"
          element={
            <Layout>
              <AdminDashboardRoute>{withSuspense(<AdminOpsDashboard />)}</AdminDashboardRoute>
            </Layout>

          }
        />
        <Route
          path="/nyafaz-brand/productlist"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <ProductList />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/servicelist"
          element={<Navigate to="/nyafaz-brand/productlist" replace />}
        />
        <Route
          path="/nyafaz-brand/add-service"
          element={<Navigate to="/nyafaz-brand/productlist" replace />}
        />
        <Route
          path="/nyafaz-brand/edit-service"
          element={<Navigate to="/nyafaz-brand/productlist" replace />}
        />
        <Route
          path="/nyafaz-brand/view-service"
          element={<Navigate to="/nyafaz-brand/productlist" replace />}
        />

        <Route
          path="/nyafaz-brand/add-product"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Addproduct />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/edit-product"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Addproduct />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/view-product"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Addproduct />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/brandflow"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Setflow />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/embed-script"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <EmbedScript />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-brand/contact-us"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Contact />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/stylists"
          element={<Navigate to="/nyafaz-brand/productlist" replace />}
        />
        <Route
          path="/nyafaz-brand/ai-analytics"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AiAnalytics />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/my-orders"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <MyOrders />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/all-users-info"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AllUsersInfo />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/whatsapp-template-manager"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <WhatsAppTemplateManager />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-brand/brand-users"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <UserList />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/organization"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <OrganizationList />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/edit-profile"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <EditProfile />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/account-billing"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AccountBilling />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/plan-details"
          element={<Navigate to="/nyafaz-brand/account-billing?tab=plan" replace />}
        />
        <Route
          path="/nyafaz-brand/wallet"
          element={<Navigate to="/nyafaz-brand/account-billing?tab=credits" replace />}
        />
        <Route
          path="/nyafaz-brand/organization-permission"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Permission />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/add-plan"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AddPlan />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/invoices"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Invoices />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-brand/subscriptions"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Subscriptions />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-brand/payment-requests"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <PaymentRequests />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-brand/admin-contact-messages"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AdminContactMessages />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-brand/sales-team/create"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<SalesTeamCreateUser />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-brand/sales-team/users"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<SalesUsersList />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-brand/sales-team/shared-links"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<SalesSharedLinks />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-brand/sales-team/request-users"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<SalesRequestUsers />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-brand/sales-team/dashboard"
          element={
            <AdminOnlyRoute>
              <Navigate to="/nyafaz-brand/sales-team/users" replace />
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-brand/subscriptions/:id"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <SubscriptionDetail />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-brand/invoice/:id"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <InvoiceDetail />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-brand/subscription-detail"
          element={<Navigate to="/nyafaz-brand/account-billing?tab=invoices" replace />}
        />
        <Route
          path="/nyafaz-brand/payments/:id"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <PaymentDetail />
              </Suspense>
            </Layout>
          }
        />
    </>
  );
}
